import React from 'react';
import { useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { actions } from '../redux/ProductReducer';
import InternalContainer from '../../layout/containers/Internal';
import { Row, Tab, Tabs } from 'react-bootstrap';
import { submit, change, destroy } from 'redux-form';
import SchemeManagementTabContainer from '../components/SchemeManagementTabContainer/SchemeManagementTabContainer';
import MainSchemeGroupDetailsForm from '../components/SchemeManagementForms/MainSchemeGroupDetailsForm/MainSchemeGroupDetailsForm';
import getIncludedResource from '../../../helpers/getIncludedResource';
import {
  getInitialValuesForConfigForm,
  getInitialValuesForDetailForm,
} from '../helpers/getInitialFormValues';

import GeneralRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/GeneralRules';
import MedicalRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/MedicalRules';

import AgeRangeRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/AgeRangeRules';
import NumTravellerRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/NumTravellerRules';
import TripDurationRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/TripDurationRules';

import GeneralDetailsForm from '../components/SchemeManagementForms/GeneralDetailsForm/GeneralDetailsForm';
import InsurerForm from '../components/SchemeManagementForms/InsurerForm/InsurerForm';
import RatesForm from '../components/SchemeManagementForms/RatesForm/RatesForm';
import CommissionForm from '../components/SchemeManagementForms/CommissionForm/CommissionForm';

import SchemeGroupExcessForm from '../components/SchemeManagementForms/ExcessForm/SchemeGroupExcessForm';

import SignpostThresholdForm from '../components/SchemeManagementForms/SignpostThresholdForm/SignpostThresholdForm';
import { InternalButtonBar } from '../../layout/components';

import { Button } from '../../../common/components';
import { difference } from '../helpers/difference';
import AgeLimitRules from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/AgeLimitRules';
import OptionsForm from '../components/SchemeManagementForms/OptionsForm/OptionsForm';
import BenefitsForm from '../components/SchemeManagementForms/BenefitsForm/BenefitsForm';
import DeclinesForm from '../components/SchemeManagementForms/DeclinesForm/DeclinesForm';
import MaxTripDurationBands from '../components/SchemeManagementForms/GeneralDetailsForm/components/Rules/MaxTripDurationBands';
import HandlersForm from '../components/SchemeManagementForms/HandlersForm/HandlersForm';
import SmsConfirmationMessageForm from '../components/SchemeManagementForms/SmsConfirmationMessageForm/SmsConfirmationMessageForm';
import SchemeNotes from '../components/SchemeManagementForms/SchemeNotes/SchemeNotes';
import SchemeAudit from '../components/SchemeManagementForms/SchemeAudit/SchemeAudit';
import RenewsToForm from '../components/SchemeManagementForms/RenewsToForm/RenewsToForm';
import CalculationServiceSchemeGroupForm from '../components/SchemeManagementForms/CaclulationServiceSchemeGroupForm/CalculationServiceSchemeGroupForm';
import MedicalScreeningRegionMapping from '../components/SchemeManagementForms/MedicalScreeningRegionMapping/MedicalScreeningRegionMapping';

const CONFIG_FORM = 'ConfigForm';
const DETAIL_FORM = 'DetailForm';

const MAIN_DETAILS_TAB = 1;
const OPTIONS_TAB = 2;
const BENEFITS_TAB = 3;
const DECLINES_TAB = 4;
const HANDLERS_TAB = 5;
const SMS_TAB = 6;
const NOTES_TAB = 7;
const AUDIT_TAB = 8;

const SchemeGroupEditContainer = ({
  params,
  getProduct,
  getInsurer,
  updateSchemeGroupConfiguration,
  updateSchemeGroup,
  getSchemeGroupDetails,
  clearSchemeGroupDetails,
  location,
  change,
  submit,
  destroy,
}) => {
  const { pathname } = location;
  const isViewOnly = pathname.indexOf('/view-scheme-group') !== -1;
  const { productId, insurerId, schemeId } = params;
  const { product, insurer, schemeGroupDetails, isSubmitting } = useSelector(
    (state) => state.productManagement,
  );
  const currentProductId = product?.data?.id;
  const currentInsurerId = insurer?.data?.id;
  const currentSchemeId = insurer?.data?.id;

  const initialConfigValues = useMemo(
    () => getInitialValuesForConfigForm(schemeGroupDetails),
    [schemeGroupDetails],
  );
  const initialDetailsValues = useMemo(
    () => getInitialValuesForDetailForm(schemeGroupDetails, true),
    [schemeGroupDetails],
  );
  useEffect(
    () => {
      if (!currentSchemeId || currentSchemeId !== schemeId) {
        getSchemeGroupDetails(schemeId);
      }
    },
    [schemeId, currentSchemeId, getSchemeGroupDetails],
  );

  useEffect(
    () => {
      if (!currentProductId || currentProductId !== productId) {
        getProduct(productId);
      }
    },
    [productId, currentProductId, currentInsurerId, getProduct],
  );

  useEffect(
    () => {
      if (!currentInsurerId || currentInsurerId !== insurerId) {
        getInsurer(insurerId, ['schemes', 'schemes.scheme_group']);
      }
    },
    [insurerId, currentInsurerId, getInsurer],
  );

  useEffect(
    () => {
      if (initialConfigValues) {
        change(CONFIG_FORM, 'meta', initialConfigValues.meta);
      }
    },
    [initialConfigValues, change],
  );

  useEffect(
    () => {
      if (initialDetailsValues) {
        change(DETAIL_FORM, 'data', initialDetailsValues.data);
        change(DETAIL_FORM, 'meta', initialDetailsValues.meta);
      }
    },
    [initialDetailsValues, change],
  );

  useEffect(() => {
    return () => {
      clearSchemeGroupDetails();
      destroy(CONFIG_FORM);
      destroy(DETAIL_FORM);
    };
  }, []);

  const baseUrl = `/admin/products/${productId}/insurers/${insurerId}`;

  const updateConfigPrefix = (newPrefix) => {
    change(CONFIG_FORM, 'meta.config.prefix', newPrefix);
  };

  const updateSchemeDetails = (formValues) => {
    const schemeDetailDifferences = difference(formValues.data, schemeGroupDetails.data);
    const schemeConfigDifferences = difference(
      formValues.meta.patch,
      schemeGroupDetails.meta,
    );
    const allValuesToUpdate = {
      data: {
        ...schemeDetailDifferences,
      },
      meta: {
        patch: {
          ...schemeConfigDifferences,
        },
      },
    };
    return Promise.resolve(updateSchemeGroup(allValuesToUpdate, schemeId)).then(() => {
      submit(CONFIG_FORM);
    });
  };

  const updateSchemeConfig = (formValues) => {
    const options = formValues.meta.config.options;
    const optionsList = options?.map((option) => {
      return option.name;
    });
    const valuesToUpdate = {
      meta: {
        ...formValues.meta,
        config: {
          ...formValues.meta.config,
          parameters: {
            ...formValues.meta.config.parameters,
            options: {
              ...formValues.meta.config.parameters.options,
              enum: optionsList,
            },
          },
        },
      },
    };
    return Promise.resolve(updateSchemeGroupConfiguration(valuesToUpdate, schemeId)).then(
      () => {
        getSchemeGroupDetails(schemeId);
      },
    );
  };

  const saveAllChanges = () => {
    submit(DETAIL_FORM);
  };

  const relatedSchemes = getIncludedResource(
    schemeGroupDetails?.data,
    schemeGroupDetails?.included,
    'all_schemes',
  );
  const claimsHandler = getIncludedResource(
    relatedSchemes?.[0],
    schemeGroupDetails?.included,
    'claims_handler',
  );
  const assistanceHandler = getIncludedResource(
    relatedSchemes?.[0],
    schemeGroupDetails?.included,
    'assistance_handler',
  );

  const renewsTo = getIncludedResource(
    relatedSchemes?.[0],
    schemeGroupDetails?.included,
    'renews_to',
  );

  const isSingle = schemeGroupDetails?.data?.attributes.group_key === 'single';
  const hasTripDurationRules = !!schemeGroupDetails?.meta?.config?.parameters
    ?.trip_duration;
  const coverLevels = schemeGroupDetails?.data?.attributes?.scheme_details.map(
    (scheme) => scheme.cover_level,
  );

  return (
    <InternalContainer title={`${isViewOnly ? 'View' : 'Edit'} Scheme Group`}>
      <Tabs unmountOnExit defaultActiveKey={1} id="scheme-management">
        <Tab
          eventKey={MAIN_DETAILS_TAB}
          title="Main Details"
          unmountOnExit={false} // keep first tab mounted to allow submit on both forms
        >
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <>
              <MainSchemeGroupDetailsForm
                relatedSchemes={relatedSchemes}
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onUpdatePrefix={updateConfigPrefix}
                onSubmit={updateSchemeDetails}
              />
              <GeneralDetailsForm
                isViewOnly={isViewOnly}
                formName={CONFIG_FORM}
                onSubmit={updateSchemeConfig}
              >
                <Row className="section-item">
                  <GeneralRules isViewOnly={isViewOnly} />
                  <AgeLimitRules isViewOnly={isViewOnly} />
                  <MedicalRules isViewOnly={isViewOnly} />
                </Row>
                <Row className="section-item">
                  <AgeRangeRules isViewOnly={isViewOnly} />
                </Row>
                <Row className="section-item">
                  <NumTravellerRules isViewOnly={isViewOnly} />
                </Row>
                <Row className="section-item">
                  <TripDurationRules
                    isViewOnly={isViewOnly}
                    isSingle={isSingle}
                    hasTripDurationRules={hasTripDurationRules}
                  />
                </Row>
                <Row className="section-item">
                  <MaxTripDurationBands isViewOnly={isViewOnly} show={!isSingle} />
                </Row>
              </GeneralDetailsForm>

              <InsurerForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />

              <RatesForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />

              <CommissionForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                maxCommission={insurer?.data?.attributes?.max_commission}
                onSubmit={updateSchemeDetails}
              />

              <SchemeGroupExcessForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />
              <SignpostThresholdForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />
              {!isSingle && (
                <RenewsToForm
                  isViewOnly={isViewOnly}
                  formName={DETAIL_FORM}
                  onSubmit={updateSchemeDetails}
                  renewsTo={renewsTo}
                />
              )}
              <CalculationServiceSchemeGroupForm
                isViewOnly={isViewOnly}
                formName={DETAIL_FORM}
                onSubmit={updateSchemeDetails}
              />
              <MedicalScreeningRegionMapping
                isViewOnly={isViewOnly}
                formName={CONFIG_FORM}
                onSubmit={updateSchemeConfig}
              />
            </>
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={OPTIONS_TAB} title="Options">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <OptionsForm
              productId={productId}
              isViewOnly={isViewOnly}
              formName={CONFIG_FORM}
              onSubmit={updateSchemeConfig}
              coverLevels={coverLevels}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={BENEFITS_TAB} title="Benefits">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <BenefitsForm
              isViewOnly={isViewOnly}
              formName={CONFIG_FORM}
              onSubmit={updateSchemeConfig}
              coverLevels={coverLevels}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={DECLINES_TAB} title="Declines">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <DeclinesForm
              isViewOnly={isViewOnly}
              formName={CONFIG_FORM}
              onSubmit={updateSchemeConfig}
              coverLevels={coverLevels}
              productType={
                product?.data?.attributes?.product_type ?? 'Leisure Travel Insurance'
              }
              medicalQuestions={product?.data?.attributes?.metadata?.medical_declaration_questions}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={HANDLERS_TAB} title="Handlers">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <HandlersForm
              isViewOnly={isViewOnly} // you can always update handlers
              formName={DETAIL_FORM}
              onSubmit={updateSchemeDetails}
              claimsHandler={claimsHandler?.data?.attributes?.name}
              assistanceHandler={assistanceHandler?.data?.attributes?.name}
              opUpdateHandler={saveAllChanges}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={SMS_TAB} title="SMS">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <SmsConfirmationMessageForm
              isViewOnly={isViewOnly}
              formName={DETAIL_FORM}
              onSubmit={updateSchemeDetails}
            />
          </SchemeManagementTabContainer>
        </Tab>
        <Tab eventKey={NOTES_TAB} title="Notes">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <SchemeNotes
              schemeId={schemeGroupDetails?.data?.id}
              resourceType={'scheme-groups'}
            />
          </SchemeManagementTabContainer>
        </Tab>

        <Tab eventKey={AUDIT_TAB} title="Audit">
          <SchemeManagementTabContainer hasScheme={!!schemeGroupDetails}>
            <SchemeAudit schemeId={schemeGroupDetails?.data?.id} />
          </SchemeManagementTabContainer>
        </Tab>
      </Tabs>
      <InternalButtonBar
        buttons={[
          {
            permissions: ['scheme.view'],
            button: <Button link buttonClass="default" to={baseUrl} label={'Back'} />,
          },
          {
            permissions: ['scheme.view'],
            button: !isViewOnly ? (
              <Button
                bsStyle="primary"
                className="pull-right"
                handleClick={saveAllChanges}
              >
                {isSubmitting ? 'Saving' : 'Save Changes'}
                {isSubmitting ? <i className="fa fa-cog fa-spin" /> : ''}
              </Button>
            ) : (
              undefined
            ),
          },
        ]}
      />
    </InternalContainer>
  );
};

export default connect(
  null,
  {
    getProduct: actions.getProduct,
    getInsurer: actions.getInsurer,
    updateSchemeGroup: actions.updateSchemeGroup,
    updateSchemeGroupConfiguration: actions.updateSchemeGroupConfiguration,
    getSchemeGroupDetails: actions.getSchemeGroupDetails,
    change: change,
    submit: submit,
    destroy: destroy,
    clearSchemeGroupDetails: actions.clearSchemeGroupDetails,
  },
)(SchemeGroupEditContainer);
